/**
 * Utility functions for masking sensitive data like emails and phone numbers
 */

/**
 * Masks an email address by replacing just two letters of the local part with "*".
 * while keeping the rest of the email visible.
 * @param email - The email address to mask
 * @returns Masked email address (e.g., "eldovtransnational262@gmail.com" -> "el**vtransnational262@gmail.com")
 */
export const maskEmail = (email: string): string => {
  if (!email || !email.includes("@")) {
    return email;
  }

  const [localPart, domain] = email.split("@");

  let maskedLocalPart: string;
  if (localPart.length <= 2) {
    maskedLocalPart = localPart.charAt(0) + "**";
  } else if (localPart.length <= 4) {
    maskedLocalPart = localPart.substring(0, 2) + "**";
  } else {
    maskedLocalPart =
      localPart.substring(0, 2) + "**" + localPart.substring(4);
  }

  return `${maskedLocalPart}@${domain}`;
};

/**
 * Masks a phone number by showing only the first 4 and last 2 digits
 * @param phone - The phone number to mask
 * @returns Masked phone number (e.g., "09012345678" -> "0901******78")
 */
export const maskPhone = (phone: string): string => {
  if (!phone || phone.length < 6) {
    return phone;
  }

  const firstFour = phone.substring(0, 4);
  const lastTwo = phone.substring(phone.length - 2);
  const middleStars = "*".repeat(phone.length - 6);

  return `${firstFour}${middleStars}${lastTwo}`;
};

/**
 * Masks an RC number by showing only the first 2 and last 2 characters
 * @param rcNumber - The RC number to mask
 * @returns Masked RC number (e.g., "RC123456" -> "RC****56")
 */
export const maskRCNumber = (rcNumber: string): string => {
  if (!rcNumber || rcNumber.length <= 4) {
    return rcNumber;
  }

  const firstTwo = rcNumber.slice(0, 2);
  const lastTwo = rcNumber.slice(-2);
  const middleMask = "*".repeat(rcNumber.length - 4);

  return `${firstTwo}${middleMask}${lastTwo}`;
};

/**
 * Masks an address by showing only the first few and last few characters
 * @param address - The address to mask
 * @returns Masked address (e.g., "123 Main Street, Lagos, Nigeria" -> "123 M************eria")
 */
export const maskAddress = (address: string): string => {
  if (!address || address.length <= 10) {
    return address;
  }

  const firstPart = address.slice(0, 5);
  const lastPart = address.slice(-4);
  const middleMask = "*".repeat(Math.min(15, address.length - 9));

  return `${firstPart}${middleMask}${lastPart}`;
};
